import { formatDate } from 'date-fns';

import PencilOutline from '@simplywallst/ui-core/icons/PencilOutline';
import { toCurrencyTruncated } from '@/utilities/formatting';

import type { CurrencyISO } from '@/constants/currencies';

import { PrimaryNarrativeIcon } from './PrimaryNarrativeIcon';

type Props = {
  companyName: string;
  currencyISO: CurrencyISO;
  primaryNarrative: {
    fairValue: number | null;
    type: string;
    narrative: {
      id: string;
      owner: {
        displayName: string | null;
        imageUrl: string | null;
      };
      updatedAt: string | null;
      title: string;
    } | null;
  };
  selectedDate: string | null;
  selectedFairValue: number | null;
};

export const PopulatedState = ({
  companyName,
  currencyISO,
  primaryNarrative,
  selectedDate,
  selectedFairValue,
}: Props) => {
  const { fairValue, type, narrative } = primaryNarrative;

  if (!narrative) {
    return null;
  }

  const targetFairValue = selectedFairValue ?? fairValue;
  const targetDate = selectedDate ?? narrative.updatedAt;

  const formattedFairValue = toCurrencyTruncated(
    targetFairValue || 0,
    2,
    currencyISO ?? 'XXX'
  );
  const defaultTitle = `I believe ${companyName}'s Fair Value is ${formattedFairValue}`;

  return (
    <div className="group/primary-narrative grid h-auto grid-rows-[max-content_auto] gap-2 sm:h-x12">
      <div className="grid grid-cols-[auto_auto] items-center justify-start gap-x1">
        <div className="grid grid-cols-[24px_max-content_auto] items-center gap-2">
          <PrimaryNarrativeIcon
            type={type.toLocaleLowerCase()}
            owner={narrative.owner}
          />
          <div>
            <span className="m-0 text-xs text-solid">{formattedFairValue}</span>
            <span className="sr-only">author fair value</span>
          </div>
          <PencilOutline className="size-x2 fill-soft group-hover/primary-narrative:fill-solid" />
        </div>
        {targetDate !== null && (
          <div>
            <span className="text-tiny text-softer">
              {formatDate(new Date(targetDate), 'dd MMM')}
            </span>
            <span className="sr-only">author updated this narrative</span>
          </div>
        )}
      </div>
      <h4 className="m-0 line-clamp-1 text-xs text-soft sm:line-clamp-3">
        {narrative.title !== '' ? narrative.title : defaultTitle}
      </h4>
    </div>
  );
};
